import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Toaster } from "sonner";
import CarServiceHeader from "../components/carservice/CarServiceHeader";
import CarServiceHero from "../components/carservice/CarServiceHero";
import CarServiceServicesGrid from "../components/carservice/CarServiceServicesGrid";
import CarServicePromise from "../components/carservice/CarServicePromise";
import CarServiceTransparency from "../components/carservice/CarServiceTransparency";
import CarServicePaymentStrip from "../components/carservice/CarServicePaymentStrip";
import TricolorAccent from "../components/carservice/TricolorAccent";
import CarServiceWhatsIncluded from "../components/carservice/CarServiceWhatsIncluded";
import CarServicePricingTiers from "../components/carservice/CarServicePricingTiers";
import CarServiceHowItWorks from "../components/carservice/CarServiceHowItWorks";
import CarServiceStatsStrip from "../components/carservice/CarServiceStatsStrip";
import CarServiceTestimonials from "../components/carservice/CarServiceTestimonials";
import CarServicePartners from "../components/carservice/CarServicePartners";
import CarServiceFAQ, { faqItems } from "../components/carservice/CarServiceFAQ";
import CarServiceCitiesStrip from "../components/carservice/CarServiceCitiesStrip";
import CarServiceFooter from "../components/carservice/CarServiceFooter";
import CarServiceBookingModal from "../components/carservice/CarServiceBookingModal";
import CarServiceMobileStickyCTA from "../components/carservice/CarServiceMobileStickyCTA";
import CarServiceWhatsAppFab from "../components/carservice/CarServiceWhatsAppFab";
import CarServiceFunnelTracker from "../components/carservice/CarServiceFunnelTracker";
import { CarServicePackageProvider } from "../components/carservice/CarServicePackageContext";
import { CarServiceCityProvider } from "../components/carservice/CarServiceCityContext";
import { trackEvent, captureUtm } from "../utils/gtag";

/**
 * /carservice — Google Ads landing page for general car service booking.
 * Single-purpose conversion funnel; isolated from the main /IN marketing nav.
 */
const CarService = () => {
  // Funnel state machine:
  //  IDLE                       — widget visible, nothing else
  //  LOOKUP_DONE_PENDING_LEAD   — modal open over widget, awaiting form/OTP
  //  LEAD_CAPTURED              — price card visible (+ modal can stay or close)
  const [lookupResult, setLookupResult] = useState(null);   // {price, vehicle}
  const [leadCaptured, setLeadCaptured] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);

  // Deep-link prefill — when the user lands here from a programmatic SEO page
  // (/car-service/{city}/{service}/{brand}/{model}), they've already declared
  // intent. Read the URL params once and feed the lookup widget so they don't
  // re-pick what they already clicked through.
  const [searchParams] = useSearchParams();
  const initialPrefill = useMemo(() => {
    const brand = searchParams.get("brand");
    const model = searchParams.get("model");
    if (!brand) return null;
    return { brand, model: model || null };
  }, [searchParams]);

  // Lookup widget completed → immediately open the lead-capture modal.
  // We deliberately do NOT reveal the price card here — that happens only
  // after lead is captured, to prevent price-shock drop-off.
  const handleLookupComplete = (price, vehicle) => {
    setLookupResult({ price, vehicle });
    setLeadCaptured(false);
    setModalOpen(true);
  };

  // Modal lead submitted successfully → flip leadCaptured so the price card
  // becomes visible behind/after the modal as the "reward".
  const handleLeadSuccess = () => {
    setLeadCaptured(true);
  };

  const handleModalClose = () => setModalOpen(false);

  // "Change car" / reset — bring the user back to the lookup widget.
  const handleResetLookup = () => {
    setLookupResult(null);
    setLeadCaptured(false);
    setModalOpen(false);
  };

  // Mobile sticky CTA routes directly to WhatsApp now (see CarServiceMobileStickyCTA).

  // Page view + UTM capture + page-specific OG/meta — fires once on mount
  useEffect(() => {
    captureUtm();
    trackEvent("view_carservice_lp");
    document.title =
      "Car Service at Home from ₹2,499 · Service in 1 Hr @ Doorstep | Wisedrive";

    // Swap OG/meta tags so social scrapers that render JS (Slack, LinkedIn,
    // Twitter X) pick up the carservice-specific share card. Server-side
    // crawlers that don't render JS still see the default tags from index.html
    // — fine, because /carservice's primary share path is paid-ads not organic.
    const setMeta = (selector, attr, value) => {
      let el = document.querySelector(selector);
      if (!el) {
        el = document.createElement("meta");
        const [k, v] = selector.replace(/^meta\[|\]$/g, "").split("=");
        el.setAttribute(k, v.replace(/^['"]|['"]$/g, ""));
        document.head.appendChild(el);
      }
      el.setAttribute(attr, value);
    };
    setMeta('meta[name="description"]', "content",
      "Doorstep general car service starting ₹2,499. OEM oil + filter, 25-point inspection, service in 1 hr at your doorstep, 1,000 km warranty. 500+ cities.");
    setMeta('meta[property="og:title"]', "content",
      "Wisedrive Car Service — ₹2,499 onwards · Service in 1 Hr @ Doorstep");
    setMeta('meta[property="og:description"]', "content",
      "Doorstep general car service. OEM parts, 25-point inspection, 1,000 km warranty. Service in 1 hr at your doorstep.");
    setMeta('meta[property="og:url"]', "content",
      "https://www.wisedrive.com/carservice");
    setMeta('meta[property="og:image"]', "content",
      "https://www.wisedrive.com/og-carservice.png");
    setMeta('meta[name="twitter:title"]', "content",
      "Wisedrive Car Service — ₹2,499 onwards");
    setMeta('meta[name="twitter:description"]', "content",
      "Doorstep car service. OEM parts. 1,000 km warranty. Service in 1 hr.");
    setMeta('meta[name="twitter:image"]', "content",
      "https://www.wisedrive.com/og-carservice.png");

    // Update canonical
    let canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) canonical.setAttribute("href", "https://www.wisedrive.com/carservice");
  }, []);

  // JSON-LD schemas — injected as <script> tags for rich-snippet eligibility
  const jsonLd = useMemo(
    () => ({
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "AutoRepair",
          "@id": "https://www.wisedrive.com/carservice#business",
          name: "Wisedrive Car Service",
          description:
            "Doorstep car service with OEM parts, certified mechanics, 25-point inspection, and 1,000 km warranty. Service in 1 hr at your doorstep.",
          url: "https://www.wisedrive.com/carservice",
          image: "https://www.wisedrive.com/og-carservice.png",
          telephone: "+917338073380",
          priceRange: "₹₹",
          address: {
            "@type": "PostalAddress",
            addressCountry: "IN",
            addressRegion: "Karnataka",
            addressLocality: "Bangalore",
          },
          areaServed: [
            { "@type": "City", name: "Bangalore" },
            { "@type": "City", name: "Delhi" },
            { "@type": "City", name: "Mumbai" },
            { "@type": "City", name: "Hyderabad" },
            { "@type": "City", name: "Chennai" },
            { "@type": "City", name: "Pune" },
          ],
          aggregateRating: {
            "@type": "AggregateRating",
            ratingValue: "4.8",
            reviewCount: "3200",
          },
          openingHoursSpecification: {
            "@type": "OpeningHoursSpecification",
            dayOfWeek: [
              "Monday", "Tuesday", "Wednesday", "Thursday",
              "Friday", "Saturday", "Sunday",
            ],
            opens: "08:00",
            closes: "21:00",
          },
        },
        {
          "@type": "Service",
          "@id": "https://www.wisedrive.com/carservice#service",
          serviceType: "General Car Service",
          provider: { "@id": "https://www.wisedrive.com/carservice#business" },
          areaServed: "India",
          offers: {
            "@type": "AggregateOffer",
            priceCurrency: "INR",
            lowPrice: "2499",
            highPrice: "4999",
            offerCount: "5",
          },
          hasOfferCatalog: {
            "@type": "OfferCatalog",
            name: "General Service Inclusions",
            itemListElement: [
              { "@type": "Offer", itemOffered: { "@type": "Service", name: "Engine oil replacement (OEM)" } },
              { "@type": "Offer", itemOffered: { "@type": "Service", name: "Oil filter replacement" } },
              { "@type": "Offer", itemOffered: { "@type": "Service", name: "Air filter replacement" } },
              { "@type": "Offer", itemOffered: { "@type": "Service", name: "Coolant top-up (200 ml)" } },
              { "@type": "Offer", itemOffered: { "@type": "Service", name: "Brake fluid top-up" } },
              { "@type": "Offer", itemOffered: { "@type": "Service", name: "Wiper / washer fluid top-up" } },
              { "@type": "Offer", itemOffered: { "@type": "Service", name: "Heater / spark plugs checking" } },
              { "@type": "Offer", itemOffered: { "@type": "Service", name: "Fuel filter checking" } },
              { "@type": "Offer", itemOffered: { "@type": "Service", name: "Battery water top-up" } },
              { "@type": "Offer", itemOffered: { "@type": "Service", name: "Front brake pads servicing" } },
              { "@type": "Offer", itemOffered: { "@type": "Service", name: "Full car OBD-II scan" } },
              { "@type": "Offer", itemOffered: { "@type": "Service", name: "Doorstep service · 1 hour turnaround" } },
            ],
          },
        },
        {
          "@type": "FAQPage",
          mainEntity: faqItems.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        },
        {
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: "https://www.wisedrive.com/" },
            { "@type": "ListItem", position: 2, name: "Car Service", item: "https://www.wisedrive.com/carservice" },
          ],
        },
      ],
    }),
    [],
  );

  return (
    <CarServicePackageProvider>
    <CarServiceCityProvider>
    <div className="min-h-screen bg-white text-slate-900 antialiased font-sans" data-testid="carservice-page">
      {/* Structured data for rich snippets */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />

      <CarServiceHeader />
      <TricolorAccent />
      <CarServiceHero
        revealedPrice={leadCaptured ? lookupResult?.price : null}
        revealedVehicle={leadCaptured ? lookupResult?.vehicle : null}
        onLookupComplete={handleLookupComplete}
        onResetLookup={handleResetLookup}
        initialPrefill={initialPrefill}
      />
      <CarServiceServicesGrid />
      <CarServicePromise />
      <CarServiceTransparency />
      <CarServicePaymentStrip />
      <CarServiceWhatsIncluded />
      <CarServicePricingTiers />
      <CarServiceHowItWorks />
      <CarServiceStatsStrip />
      <CarServiceTestimonials />
      <CarServicePartners />
      <CarServiceFAQ />
      <CarServiceCitiesStrip />
      <CarServiceFooter />

      <CarServiceFunnelTracker />

      <CarServiceMobileStickyCTA visible={!modalOpen} />
      <CarServiceWhatsAppFab />

      <CarServiceBookingModal
        open={modalOpen}
        price={lookupResult?.price}
        vehicle={lookupResult?.vehicle}
        onLeadSuccess={handleLeadSuccess}
        onClose={handleModalClose}
      />

      <Toaster position="top-center" richColors closeButton />
    </div>
    </CarServiceCityProvider>
    </CarServicePackageProvider>
  );
};

export default CarService;
